import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createNewsletterSubscriptionRequest,
  getNewsletterSubscriptions,
  removeNewsletterSubscription,
  updateNewsletterSubscriptionRequest,
} from "../../../../services/api/landing/newsletterSubscription/newsletterSubscriptionApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: {
    en: "Newsletter subscription created successfully!",
    ar: "تم إنشاء اشتراك النشرة بنجاح!",
  },
  addError: {
    en: "Failed to create newsletter subscription.",
    ar: "فشل في إنشاء اشتراك النشرة.",
  },
  update: {
    en: "Newsletter subscription updated successfully!",
    ar: "تم تحديث اشتراك النشرة بنجاح!",
  },
  updateError: {
    en: "Failed to update newsletter subscription.",
    ar: "فشل في تحديث اشتراك النشرة.",
  },
  delete: {
    en: "Newsletter subscription deleted successfully!",
    ar: "تم حذف اشتراك النشرة بنجاح!",
  },
  deleteError: {
    en: "Failed to delete newsletter subscription.",
    ar: "فشل في حذف اشتراك النشرة.",
  },
};

export const fetchNewsletterSubscriptions = createAsyncThunk(
  "newsletterSubscriptions/fetchNewsletterSubscriptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getNewsletterSubscriptions();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch Newsletter Subscriptions");
    }
  },
);

export const createNewsletterSubscription = createAsyncThunk(
  "newsletterSubscriptions/createNewsletterSubscription",
  async (subscriptionData, { rejectWithValue }) => {
    try {
      const response =
        await createNewsletterSubscriptionRequest(subscriptionData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create Newsletter Subscription");
    }
  },
);

export const updateNewsletterSubscription = createAsyncThunk(
  "newsletterSubscriptions/updateNewsletterSubscription",
  async ({ id, subscriptionData }, { rejectWithValue }) => {
    try {
      const response = await updateNewsletterSubscriptionRequest(
        id,
        subscriptionData,
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update Newsletter Subscription");
    }
  },
);

export const deleteNewsletterSubscription = createAsyncThunk(
  "newsletterSubscriptions/deleteNewsletterSubscription",
  async (id, { rejectWithValue }) => {
    try {
      await removeNewsletterSubscription(id);
      return id;
    } catch (error) {
      return rejectWithValue("Failed to delete Newsletter Subscription");
    }
  },
);

const newsletterSubscriptionSlice = createSlice({
  name: "newsletterSubscriptions",
  initialState: {
    subscriptionData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewsletterSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNewsletterSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptionData = action.payload;
      })
      .addCase(fetchNewsletterSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createNewsletterSubscription.fulfilled, (state, action) => {
        state.subscriptionData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createNewsletterSubscription.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateNewsletterSubscription.fulfilled, (state, action) => {
        const index = state.subscriptionData.findIndex(
          (sub) => sub.id === action.payload.id,
        );
        if (index !== -1) {
          state.subscriptionData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateNewsletterSubscription.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteNewsletterSubscription.fulfilled, (state, action) => {
        state.subscriptionData = state.subscriptionData.filter(
          (sub) => sub.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteNewsletterSubscription.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default newsletterSubscriptionSlice.reducer;
